import React from "react";
import { Box, Typography, Card, CardContent, Alert, Tooltip } from "@mui/material";
import { THEME_PRIMARY, THEME_PRIMARY_LIGHT, THEME_PRIMARY_TINT } from "../bookingTheme";
import { resolvePreviewSrc } from "../../../utils/mediaUtils";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import CurrentVehicleSummaryCard from "./CurrentVehicleSummaryCard";
import {
  formatCurrentVehicleDetailValue,
  type CurrentBookingVehicleSummary,
} from "./bookingVehicleSummaryUtils";

export interface VehicleModelOption {
  id: string | number;
  name: string;
  speed: string;
  weight: string;
  available: string;
  imageUrl?: string;
  /** From hub vehicle EV speed type (e.g. high-speed models). */
  isHighSpeed?: boolean;
}

interface SelectVehicleModelStepProps {
  options: VehicleModelOption[];
  selectedId: string | number | null;
  onSelect: (id: string | number) => void;
  loading?: boolean;
  selectedHubName?: string | null;
  /** Same as Select Vehicle step — from booking + GetVehicleById */
  currentBookingVehicle?: CurrentBookingVehicleSummary | null;
  /** When KYC is incomplete or DL OCR full name is missing, high-speed models cannot be selected */
  kycBlocksHighSpeedModels?: boolean;
  kycCheckLoading?: boolean;
  kycHighSpeedMessage?: string;
}

const SelectVehicleModelStep: React.FC<SelectVehicleModelStepProps> = ({
  options,
  selectedId,
  onSelect,
  loading = false,
  selectedHubName,
  currentBookingVehicle = null,
  kycBlocksHighSpeedModels = false,
  kycCheckLoading = false,
  kycHighSpeedMessage = "Driving licence name (DL OCR) is required for high-speed models.",
}) => {
  const hubLabel = selectedHubName?.trim() || "";
  const showYourSelection = Boolean(hubLabel);

  const selectedModelName =
    selectedId != null && selectedId !== ""
      ? (options.find((o) => String(o.id) === String(selectedId))?.name ?? "").trim()
      : "";

  const showCurrentVehicle = Boolean(currentBookingVehicle);
  const showSummaryRow = showYourSelection || showCurrentVehicle;

  const selectionSummaryRow = (label: string, value: string) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(108px, 38%) minmax(0, 1fr)", sm: "118px minmax(0, 1fr)" },
        columnGap: { xs: 1.25, sm: 1.5 },
        alignItems: "center",
        py: { xs: 0.75, sm: 0.625 },
        borderBottom: "1px solid rgba(22, 67, 135, 0.1)",
        "&:last-of-type": { borderBottom: "none", pb: 0 },
        "&:first-of-type": { pt: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          lineHeight: 1.35,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  );

  const summaryCardSx = {
    border: "1px solid rgba(22, 67, 135, 0.2)",
    borderRadius: 2,
    bgcolor: THEME_PRIMARY_TINT,
    px: { xs: 1.5, sm: 1.75 },
    py: { xs: 1.25, sm: 1.125 },
    boxShadow: "0 1px 2px rgba(22, 67, 135, 0.06)",
    minWidth: 0,
    width: "100%",
  } as const;

  const hasHighSpeedInList = options.some((o) => o.isHighSpeed === true);
  const showKycHighSpeedWarning =
    !kycCheckLoading && kycBlocksHighSpeedModels && hasHighSpeedInList;

  const summaryCardTitleSx = {
    display: "block",
    fontWeight: 700,
    color: THEME_PRIMARY,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    mb: 0.75,
    pb: 0.5,
    borderBottom: `2px solid ${THEME_PRIMARY}`,
  } as const;

  return (
    <Box sx={{ mb: 1, minWidth: 0 }}>


      {showSummaryRow ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: showYourSelection && showCurrentVehicle ? "repeat(2, minmax(0, 1fr))" : "1fr",
            },
            gap: { xs: 1.75, sm: 2 },
            width: "100%",
            maxWidth: { sm: showYourSelection && showCurrentVehicle ? 920 : 480 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            alignItems: "stretch",
          }}
        >
          {showCurrentVehicle && currentBookingVehicle ? (
            <CurrentVehicleSummaryCard vehicle={currentBookingVehicle} />
          ) : null}
          {showYourSelection ? (
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={summaryCardTitleSx}>
                Your selection
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {selectionSummaryRow("Hub", formatCurrentVehicleDetailValue(hubLabel))}
                {selectionSummaryRow("Model", formatCurrentVehicleDetailValue(selectedModelName))}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: 18, sm: 20 },
          lineHeight: 1.35,
          width: "100%",
          mb: showSummaryRow ? { xs: 1.5, sm: 1.75 } : { xs: 2, sm: 2.5 },
        }}
      >
        Please Select Vehicle Model
      </Typography>
      {loading ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>Loading vehicle models...</Typography>
      ) : (
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          {showKycHighSpeedWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {kycHighSpeedMessage}
              </Typography>
            </Alert>
          )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
            gap: { xs: 1.5, sm: 2 },
            alignItems: "stretch",
          }}
        >
          {options.map((model) => {
            const isSelected = selectedId !== null && String(selectedId) === String(model.id);
            const isHighSpeedModel = model.isHighSpeed === true;
            const selectionDisabled =
              !kycCheckLoading && kycBlocksHighSpeedModels && isHighSpeedModel;
            const card = (
              <Card
                onClick={() => {
                  if (selectionDisabled) return;
                  onSelect(model.id);
                }}
                sx={{
                  cursor: selectionDisabled ? "not-allowed" : "pointer",
                  border: "1px solid",
                  borderColor: isSelected ? THEME_PRIMARY_LIGHT : "rgba(22, 67, 135, 0.25)",
                  borderRadius: "10px",
                  bgcolor: isSelected ? "#E8E8FF" : "#fff",
                  boxShadow: "none",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  flex: 1,
                  minHeight: { xs: 130, sm: 160 },
                  opacity: selectionDisabled ? 0.55 : 1,
                  pointerEvents: selectionDisabled ? "none" : "auto",
                  "&:hover": selectionDisabled
                    ? {}
                    : { borderColor: THEME_PRIMARY_LIGHT, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                }}
              >
                <CardContent
                  sx={{
                    p: 0,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    height: "100%",
                    "&:last-child": { pb: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flex: "1 1 auto",
                      minHeight: 0,
                      alignItems: "stretch",
                      gap: { xs: 1.5, sm: 2 },
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1.25, sm: 1.5 },
                    }}
                  >
                    <Box
                      sx={{
                        flex: "2 1 0",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: THEME_PRIMARY,
                          fontSize: "1em",
                          mb: 1.25,
                          lineHeight: 1.4,
                        }}
                      >
                        {model.name}
                      </Typography>
                      <Typography sx={{ color: "#666666", fontSize: "1rem", fontWeight: 400, display: "block", lineHeight: 1.6 }}>
                        Speed: {model.speed}
                      </Typography>
                      <Typography sx={{ color: "#666666", fontSize: "1rem", fontWeight: 400, display: "block", lineHeight: 1.6 }}>
                        Weight: {model.weight}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "1 1 0",
                        maxWidth: "33%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        alignSelf: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 96,
                          flexShrink: 0,
                          borderRadius: "10px",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={resolvePreviewSrc(model.imageUrl, DefaultImage)}
                          alt={model.name}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = DefaultImage;
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      mt: "auto",
                      px: { xs: 1.5, sm: 2 },
                      pb: { xs: 1.25, sm: 1.5 },
                      pt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: "9999px",
                        bgcolor: "#E0FFE0",
                        display: "inline-block",
                      }}
                    >
                      <Typography sx={{ color: "#154D0E", fontSize: "0.875rem", fontWeight: 600 }}>
                        {model.available}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
            return (
              <Box
                key={model.id}
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {selectionDisabled ? (
                  <Tooltip title={kycHighSpeedMessage} placement="top" arrow>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      {card}
                    </Box>
                  </Tooltip>
                ) : (
                  card
                )}
              </Box>
            );
          })}
        </Box>
        </Box>
      )}
    </Box>
  );
};

export default SelectVehicleModelStep;
